import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../containers/customer/customers.theme';
import {
  CustomersKeyframes,
  PeopleIcon,
  PrimaryButton,
} from '../containers/customer/CustomersUI';
import ConfirmDialog from '../containers/admin/ConfirmDialog';

import VendorList from '../containers/vendor/VendorList';
import VendorLedger from '../containers/vendor/VendorLedger';
import VendorFormDialog from '../containers/vendor/VendorFormDialog';
import VendorTransactionDialog from '../containers/vendor/VendorTransactionDialog';
import { COPY } from '../containers/vendor/vendors.copy';
import {
  addVendor,
  addVendorTransaction,
  removeVendorTransaction,
  replaceVendorTransaction,
  setVendorDataFetched,
  setVendorTransactions,
  setVendors,
  updateVendor,
} from '../containers/vendor/vendorSlice';
import {
  createTransaction,
  createVendor,
  deleteTransaction,
  fetchTransactions,
  fetchVendors,
  updateTransaction,
  updateVendor as patchVendor,
  withRunningBalance,
} from '../containers/vendor/vendorsApi';

/**
 * The vendors book — who the shop owes, and what for.
 *
 * The customers book pointed the other way, and built the same way: the list
 * stays put on the left and the ledger beside it changes, because working a
 * book means hopping between names.
 *
 * The thing worth knowing is that this screen did not invent its own ledger.
 * Every expense filed on invoice already writes a vendor transaction — that is
 * what raises a supplier's balance — so those lines were being recorded long
 * before there was anywhere to see them. This is that view, plus the ability
 * to pay one down.
 *
 * There is no delete: the API has no route for it. A supplier that is finished
 * with can have its statement closed, which is the phone's way, and is not
 * wired up here yet.
 */

/** The roles the vendor and transaction routes let through. */
const ALLOWED_ROLES = ['manager', 'cashier', 'accountant', 'superadmin-superadmin'];

export default function Vendors() {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const { vendors, isVendorDataFetched, transactionsByVendor } = useSelector(
    (state) => state.vendors
  );

  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState(null);
  const [loadingList, setLoadingList] = React.useState(false);
  const [loadingLedger, setLoadingLedger] = React.useState(false);
  const [listError, setListError] = React.useState('');

  const [transactionKind, setTransactionKind] = React.useState(null);
  const [editingTransaction, setEditingTransaction] = React.useState(null);
  const [deletingTransaction, setDeletingTransaction] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const allowed = ALLOWED_ROLES.indexOf(activeUser?.role) !== -1;
  const currency = business?.currencyMark || '$';
  const selected = vendors.find((vendor) => vendor._id === selectedId) || null;
  const transactions = selectedId ? transactionsByVendor[selectedId] || [] : [];

  /* ── the list ───────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (!allowed || isVendorDataFetched || !business?._id) return undefined;

    let cancelled = false;
    setLoadingList(true);

    fetchVendors({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setVendors(rows));
        dispatch(setVendorDataFetched(true));
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err?.response?.data?.message || 'Could not load vendors');
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed, isVendorDataFetched, business, token, dispatch]);

  /* ── one vendor's book, fetched only when it is opened ──────────────── */

  const loadLedger = React.useCallback(
    (vendorId, { force } = {}) => {
      if (!vendorId) return;
      if (!force && transactionsByVendor[vendorId]) return;

      setLoadingLedger(true);
      fetchTransactions({ vendorId, token })
        .then((rows) => {
          dispatch(
            setVendorTransactions({ vendorId, transactions: withRunningBalance(rows) })
          );
        })
        .catch(() => {
          dispatch(setVendorTransactions({ vendorId, transactions: [] }));
        })
        .finally(() => setLoadingLedger(false));
    },
    [transactionsByVendor, token, dispatch]
  );

  const openVendor = (vendor) => {
    setSelectedId(vendor._id);
    loadLedger(vendor._id);
  };

  /* ── writing ────────────────────────────────────────────────────────── */

  /**
   * One handler for adding and for correcting, because the dialog is the same
   * dialog — it passes back the line it was editing, or nothing for a new one.
   *
   * The running balance belongs to the whole list rather than to any one line,
   * so after either it is recomputed from scratch instead of patched.
   */
  const submitTransaction = async ({ kind, amount, description, date, transaction }) => {
    if (transaction) {
      const saved = await updateTransaction({
        id: transaction._id,
        kind,
        amount,
        description,
        date,
        token,
      });

      const merged = { ...transaction, ...(saved || {}) };
      dispatch(replaceVendorTransaction({ vendorId: selectedId, transaction: merged }));

      const next = withRunningBalance(
        (transactionsByVendor[selectedId] || []).map((entry) =>
          entry._id === transaction._id ? merged : entry
        )
      );
      dispatch(setVendorTransactions({ vendorId: selectedId, transactions: next }));
      return;
    }

    const created = await createTransaction({
      vendorId: selectedId,
      userId: activeUser?._id || activeUser?.id,
      kind,
      amount,
      description,
      date,
      token,
    });

    dispatch(addVendorTransaction({ vendorId: selectedId, transaction: created }));

    const next = withRunningBalance([...(transactionsByVendor[selectedId] || []), created]);
    dispatch(setVendorTransactions({ vendorId: selectedId, transactions: next }));
  };

  const removeTransaction = async () => {
    await deleteTransaction({ id: deletingTransaction._id, token });

    dispatch(
      removeVendorTransaction({
        vendorId: selectedId,
        transactionId: deletingTransaction._id,
      })
    );

    const next = withRunningBalance(
      (transactionsByVendor[selectedId] || []).filter(
        (entry) => entry._id !== deletingTransaction._id
      )
    );
    dispatch(setVendorTransactions({ vendorId: selectedId, transactions: next }));
  };

  const submitNewVendor = async (payload) => {
    const created = await createVendor({
      payload: { ...payload, business: business?._id },
      token,
    });
    dispatch(addVendor(created));
    if (created?._id) {
      setSelectedId(created._id);
      dispatch(setVendorTransactions({ vendorId: created._id, transactions: [] }));
    }
  };

  const submitEdit = async (payload) => {
    const saved = await patchVendor({ id: editing._id, payload, token });
    dispatch(updateVendor({ _id: editing._id, updatedVendor: saved || payload }));
  };

  /* ── search ─────────────────────────────────────────────────────────── */

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = vendors.slice().sort((a, b) => {
      // Most recently touched first, as the phone sorts it.
      const at = new Date(a.updated_at || a.updatedAt || 0).getTime();
      const bt = new Date(b.updated_at || b.updatedAt || 0).getTime();
      return bt - at;
    });
    if (!q) return rows;
    return rows.filter(
      (vendor) =>
        String(vendor.name || '').toLowerCase().indexOf(q) !== -1 ||
        String(vendor.phone || '').toLowerCase().indexOf(q) !== -1 ||
        String(vendor.agent || '').toLowerCase().indexOf(q) !== -1 ||
        String(vendor.city || '').toLowerCase().indexOf(q) !== -1
    );
  }, [vendors, query]);

  /* ── not for waiters ────────────────────────────────────────────────── */

  if (!allowed) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <span style={{ fontSize: 14, color: T.inkSoft }}>
          Only a manager or a cashier can open Vendors.
        </span>
      </div>
    );
  }

  /* ── the book is empty: nothing to list beside ──────────────────────── */

  if (isVendorDataFetched && !vendors.length) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <CustomersKeyframes />
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            border: `1.5px dashed ${T.borderStrong}`,
            borderRadius: T.radiusPill,
            color: T.inkGhost,
          }}
        >
          <PeopleIcon />
        </span>
        <span style={{ fontSize: 16, color: T.inkSoft }}>{COPY.none}</span>
        <PrimaryButton onClick={() => setCreating(true)} style={{ padding: '0 24px' }}>
          {COPY.addLong}
        </PrimaryButton>

        {creating ? (
          <VendorFormDialog onClose={() => setCreating(false)} onSubmit={submitNewVendor} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      <VendorList
        vendors={visible}
        currency={currency}
        query={query}
        onQueryChange={setQuery}
        selectedId={selectedId}
        onOpen={openVendor}
        onAdd={() => setCreating(true)}
        loading={loadingList}
      />

      {listError ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 40,
            textAlign: 'center',
          }}
        >
          <span style={{ fontSize: 14, color: T.danger }}>{listError}</span>
        </div>
      ) : (
        <VendorLedger
          vendor={selected}
          transactions={transactions}
          loading={loadingLedger}
          currency={currency}
          onPayment={() => setTransactionKind('payment')}
          onCharge={() => setTransactionKind('charge')}
          onEdit={setEditing}
          onEditTransaction={setEditingTransaction}
          onDeleteTransaction={setDeletingTransaction}
        />
      )}

      {transactionKind && selected ? (
        <VendorTransactionDialog
          kind={transactionKind}
          vendor={selected}
          currency={currency}
          onClose={() => setTransactionKind(null)}
          onSubmit={submitTransaction}
        />
      ) : null}

      {editingTransaction && selected ? (
        <VendorTransactionDialog
          // Which side it sits on comes from the line itself, not from a
          // choice — a correction never flips a bill into a payment.
          kind={Number(editingTransaction.debit || 0) > 0 ? 'charge' : 'payment'}
          vendor={selected}
          currency={currency}
          transaction={editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onSubmit={submitTransaction}
        />
      ) : null}

      {deletingTransaction && selected ? (
        <ConfirmDialog
          title={COPY.deleteTitle}
          body={COPY.deleteBody}
          note={COPY.deleteNote}
          confirmLabel={COPY.delete}
          cancelLabel={COPY.cancel}
          onClose={() => setDeletingTransaction(null)}
          onConfirm={removeTransaction}
        />
      ) : null}

      {creating ? (
        <VendorFormDialog onClose={() => setCreating(false)} onSubmit={submitNewVendor} />
      ) : null}

      {editing ? (
        <VendorFormDialog
          vendor={editing}
          onClose={() => setEditing(null)}
          onSubmit={submitEdit}
        />
      ) : null}
    </div>
  );
}
